import { useId } from 'react';
import { useIntl } from 'react-intl';
import type { AdminPortalConnectionResponse, PortalConnectionStatus } from '@immo/api-contract';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';

const DASH = '—';

const STATUS_KEY: Record<PortalConnectionStatus, string> = {
  NOT_CONNECTED: 'status.not-connected',
  CONNECTED: 'status.connected',
  EXPIRED: 'status.expired',
};

type PortalConnectionsSectionProps = {
  readonly connections: ReadonlyArray<AdminPortalConnectionResponse> | null;
};

export function PortalConnectionsSection({ connections }: PortalConnectionsSectionProps) {
  const t = useTranslations('pages.admin.workspaces.detail.portals');
  const canRead = useCan('backoffice:property:property-read');

  if (!canRead) return null;

  return (
    <section className="space-y-4">
      <SectionHeading>{t('section')}</SectionHeading>
      {connections === null ? (
        <p className="text-sm text-danger-600">{t('load-error')}</p>
      ) : (
        connections.map((connection) => <PortalBlock key={connection.portal} connection={connection} />)
      )}
    </section>
  );
}

function PortalBlock({ connection }: { readonly connection: AdminPortalConnectionResponse }) {
  const t = useTranslations('pages.admin.workspaces.detail.portals');
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <h3 id={headingId} className="text-sm font-semibold text-text-primary">
        {t(`portal.${connection.portal}`)}
      </h3>
      <Rows connection={connection} />
    </section>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

function DefRow({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-text-primary">{children}</dd>
    </>
  );
}

const Dash = () => <span className="text-text-placeholder">{DASH}</span>;

function Rows({ connection }: { readonly connection: AdminPortalConnectionResponse }) {
  const t = useTranslations('pages.admin.workspaces.detail.portals');
  const intl = useIntl();
  const formatDateTime = (value: string) => intl.formatDate(value, { dateStyle: 'medium', timeStyle: 'short' });
  const openImmo = connection.portal !== 'IMMOSCOUT24';
  const { status, accountName, connectedBy, connectedAt, expiredAt, lastError } = connection;

  return (
    <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
      <DefRow label={t('status-label')}>
        {openImmo && status === 'EXPIRED' ? t('status.invalid') : t(STATUS_KEY[status])}
        {!openImmo &&
          status === 'EXPIRED' &&
          expiredAt !== null &&
          ` ${t('expired-since', { at: formatDateTime(expiredAt) })}`}
      </DefRow>
      {!openImmo && <DefRow label={t('account-label')}>{accountName ?? <Dash />}</DefRow>}
      <DefRow label={t(openImmo ? 'entered-by-label' : 'connected-by-label')}>
        {connectedBy === null ? (
          <Dash />
        ) : (
          <>
            {connectedBy.displayName}
            {connectedBy.displayName !== connectedBy.email && (
              <span className="text-text-tertiary"> · {connectedBy.email}</span>
            )}
          </>
        )}
      </DefRow>
      <DefRow label={t(openImmo ? 'entered-at-label' : 'connected-at-label')}>
        {connectedAt === null ? <Dash /> : formatDateTime(connectedAt)}
      </DefRow>
      {openImmo && (
        <DefRow label={t('invalid-since-label')}>{expiredAt === null ? <Dash /> : formatDateTime(expiredAt)}</DefRow>
      )}
      <DefRow label={t('last-error-label')}>
        {lastError === null ? (
          t('last-error-none')
        ) : (
          <>
            <span className="block whitespace-pre-wrap break-words font-mono text-sm">{lastError.message}</span>
            <span className="block text-sm text-text-tertiary">
              {t('last-error-note')} · {formatDateTime(lastError.at)}
            </span>
          </>
        )}
      </DefRow>
    </dl>
  );
}
